// components/socials/profile/Account.jsx
import { signOutWithNotifications } from "../../../firebase/notificationSession";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiArrowUp,
  FiChevronLeft,
  FiChevronRight,
  FiLogOut,
  FiX,
  FiTrash2,
  FiUpload,
} from "react-icons/fi";
import { Purchases } from "@revenuecat/purchases-js";

import { auth, db } from "../../../firebase/firebase.utils";
import useImageUpload from "../../../hooks/useImageUpload";
import UploadOverlay from "../../upload/UploadOverlay";
import { setCurrentUser } from "../../../redux/user/user.actions";
import { setTheme } from "../../../redux/theme/theme.actions";
import { ReactComponent as ArrowDownRight } from "../../../assets/arrows/arrowDownRight.svg";
import { ReactComponent as ArrowDownSlightRight } from "../../../assets/arrows/arrowDownSlightRight.svg";
import AvatarCreator from "../../avatar-creator/AvatarCreator";
import Peep from "../../avatar-creator/Peep";
import AccountPostsManager from "./AccountPostsManager";
import {
  humanizeTagline,
  TAGLINE_OPTIONS,
  TAGS_BY_TAGLINE_KEY,
} from "./profileOptions";

const NOTIFICATION_GROUPS = [
  {
    title: "Messages",
    rows: [
      ["Private messages", "privateMsg"],
      ["Project messages", "projectMsg"],
      ["Workspace messages", "workspaceMsg"],
      ["Comments on assigned tasks", "commentAssigned"],
      ["Comments on tasks you created", "commentCreated"],
    ],
  },
  {
    title: "Tasks and notes",
    rows: [
      ["Task assigned", "taskAssigned"],
      ["Note assigned", "noteAssigned"],
      ["Task starts", "taskStart"],
      ["Task ends", "taskEnd"],
    ],
  },
  {
    title: "Meetings",
    rows: [
      ["New meeting", "meetingNew"],
      ["Meeting starts", "meetingStart"],
      ["Meeting cancelled", "meetingCancel"],
      ["Meeting postponed", "meetingMoved"],
    ],
  },
];

const THEME_OPTIONS = [
  ["craft-theme", "Craft"],
  ["paper-theme", "Paper"],
  ["rouge-theme", "Plum"],
  ["dark-theme", "Dark"],
];

const cleanPhotos = (value) =>
  (Array.isArray(value) ? value : value ? [value] : []).filter(Boolean);

const tagsFromText = (value = "") =>
  value
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean)
    .slice(0, 12);

export default function Account() {
  const currentUser = useSelector((state) => state.user.currentUser);
  const currentEntitlement = useSelector((state) => state.user.entitlement);
  const theme = useSelector((state) => state.theme.theme);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [slide, setSlide] = useState(0);
  const [saving, setSaving] = useState(false);
  const { uploading, progress, status, uploadImages, uploadImage } =
    useImageUpload({
      userId: currentUser?.uid,
      getBasePath: () =>
        currentUser?.uid ? `users/${currentUser.uid}/profile` : "",
      getParentPath: () => `users/${currentUser.uid}`,
    });
  const [message, setMessage] = useState("");
  const [membershipUrl, setMembershipUrl] = useState(null);
  const [showTaglineModal, setShowTaglineModal] = useState(false);
  const [draftTaglineKey, setDraftTaglineKey] = useState("");
  const [draftTags, setDraftTags] = useState([]);
  const [customTagline, setCustomTagline] = useState("");
  const [customTag, setCustomTag] = useState("");

  useEffect(() => {
    if (!currentUser?.uid) return;
    Purchases.getSharedInstance()
      .getCustomerInfo()
      .then((customerInfo) =>
        setMembershipUrl(customerInfo.managementURL || null),
      )
      .catch(() => setMembershipUrl(null));
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser) return;
    setForm({
      userName: currentUser.userName || "",
      headline: currentUser.headline || "",
      bio: currentUser.bio || "",
      selectedTaglineKey: currentUser.selectedTaglineKey || "",
      selectedTagsText: (currentUser.selectedTags || []).join(", "),
      profilePostsDisplay:
        currentUser.profilePostsDisplay === "list" ? "list" : "grid",
      imageUrls: cleanPhotos(currentUser.imageUrl),
      showAvatar: currentUser.preferences?.showAvatar !== false,
      notfSettings: { ...(currentUser.notfSettings || {}) },
    });
  }, [currentUser]);

  const photos = useMemo(() => form?.imageUrls || [], [form?.imageUrls]);
  const slides = useMemo(
    () => [
      { type: "avatar", id: "avatar" },
      ...photos.map((url, index) => ({
        type: "photo",
        id: `${url}-${index}`,
        url,
      })),
    ],
    [photos],
  );

  useEffect(() => {
    setSlide((current) => Math.min(current, Math.max(0, slides.length - 1)));
  }, [slides.length]);

  const setField = (field, value) =>
    setForm((previous) => ({ ...previous, [field]: value }));

  const setNotification = (key, value) =>
    setForm((previous) => ({
      ...previous,
      notfSettings: { ...previous.notfSettings, [key]: value },
    }));

  const openTaglineModal = () => {
    setDraftTaglineKey(form.selectedTaglineKey);
    setDraftTags(tagsFromText(form.selectedTagsText));
    setCustomTagline("");
    setCustomTag("");
    setShowTaglineModal(true);
  };

  const closeTaglineModal = () => setShowTaglineModal(false);

  const canUseCustomTaglines =
    currentEntitlement === "antPro" || currentEntitlement === "antBusiness";

  const requestTaglineUpgrade = () => {
    window.alert("Custom taglines are available with Ant Pro or Ant Business.");
    navigate("/?interested=pro");
  };

  const chooseTagline = (value) => {
    setDraftTaglineKey(value);
    setDraftTags([]);
  };

  const toggleDraftTag = (tag) => {
    setDraftTags((previous) =>
      previous.includes(tag)
        ? previous.filter((item) => item !== tag)
        : [...previous, tag].slice(0, 12),
    );
  };

  const addCustomTagline = () => {
    const value = customTagline.trim();
    if (!value) return;
    if (!canUseCustomTaglines) return requestTaglineUpgrade();
    setDraftTaglineKey(value);
    setDraftTags([]);
    setCustomTagline("");
  };

  const addCustomTag = () => {
    const value = customTag.trim();
    if (!value || draftTags.includes(value)) return;
    if (!canUseCustomTaglines) return requestTaglineUpgrade();
    setDraftTags((previous) => [...previous, value].slice(0, 12));
    setCustomTag("");
  };

  const confirmTagline = () => {
    setForm((previous) => ({
      ...previous,
      selectedTaglineKey: draftTaglineKey,
      selectedTagsText: draftTags.join(", "),
    }));
    closeTaglineModal();
  };

  const removeTagline = () => {
    setForm((previous) => ({
      ...previous,
      selectedTaglineKey: "",
      selectedTagsText: "",
    }));
    closeTaglineModal();
  };

  const choosePrimary = (showAvatar) => {
    if (!showAvatar && !photos.length) return;
    setField("showAvatar", showAvatar);
    setSlide(showAvatar ? 0 : 1);
  };

  const uploadPhotos = async (event) => {
    const input = event.target;
    const files = [...(input.files || [])].filter((file) =>
      file.type.startsWith("image/"),
    );
    input.value = "";
    if (!files.length || !currentUser?.uid) return;
    if (uploading || saving) return;
    if (photos.length >= 6) {
      window.alert("You can keep up to 6 profile photos.");
      return;
    }

    try {
      const result = await uploadImages(files.slice(0, 6 - photos.length));
      const urls = result.files.map((file) => file.url);
      setForm((previous) => ({
        ...previous,
        imageUrls: [...previous.imageUrls, ...urls].slice(0, 6),
      }));
      if (!photos.length && urls.length) setSlide(1);
      if (result.errors.length)
        window.alert(
          `${result.errors.length} photo(s) could not be uploaded. Successful photos are kept; use Save changes to save them.`,
        );
    } catch (error) {
      console.log("Profile photo upload error:", error);
      window.alert("The photo could not be uploaded.");
    }
  };

  const removePhoto = (photoIndex) => {
    if (uploading) return;
    const nextPhotos = photos.filter((_, index) => index !== photoIndex);
    setForm((previous) => ({
      ...previous,
      imageUrls: nextPhotos,
      showAvatar: nextPhotos.length ? previous.showAvatar : true,
    }));
  };

  const save = async () => {
    if (!currentUser?.uid || !form || saving || uploading) return;
    const selectedTags = tagsFromText(form.selectedTagsText);
    const update = {
      userName: form.userName.trim(),
      headline: form.headline.trim(),
      bio: form.bio.trim(),
      selectedTaglineKey: form.selectedTaglineKey.trim(),
      selectedTags,
      profilePostsDisplay: form.profilePostsDisplay,
      imageUrl: form.imageUrls,
      preferences: {
        ...(currentUser.preferences || {}),
        showAvatar: form.showAvatar,
      },
      notfSettings: form.notfSettings,
    };

    if (!update.userName) {
      window.alert("Please enter a name.");
      return;
    }

    try {
      setSaving(true);
      setMessage("");
      await db
        .collection("users")
        .doc(currentUser.uid)
        .set(update, { merge: true });
      dispatch(setCurrentUser({ ...currentUser, ...update }));
      setMessage("Changes saved.");
    } catch (error) {
      console.log("Account save error:", error);
      window.alert("Your changes could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const signOut = async () => {
    if (!(await signOutWithNotifications())) return;
    navigate("/signin");
  };

  const changeTheme = (nextTheme) => {
    dispatch(setTheme(nextTheme));
    document.body.setAttribute("data-theme", nextTheme);
    localStorage.setItem("selectedTheme", nextTheme);
    window.dispatchEvent(
      new CustomEvent("antlogy-theme-change", { detail: nextTheme }),
    );
  };

  // const openPage = (path) => {
  //   // setShowPage(false);
  //   navigate(path);
  // };

  if (!currentUser || !form) {
    return (
      <main className="social-route-page social-route-page--center">
        Loading account…
      </main>
    );
  }

  const activeSlide = slides[slide] || slides[0];

  return (
    <main className="social-route-page account-page account-page--editor">
      <div className="account-page__layout">
        <aside className="account-page__sidebar">
          <section className="account-preview-card">
            <div className="account-preview-card__media">
              <header className="account-preview-card__topbar">
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => navigate(-1)}
                  aria-label="Go back"
                >
                  <FiChevronLeft />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/profile/${currentUser.uid}/connections`)
                  }
                >
                  Connections
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/profile/${currentUser.uid}`)}
                >
                  Public profile
                </button>
              </header>

              <AnimatePresence initial={false}>
                <motion.div
                  className="account-preview-card__slide"
                  key={activeSlide.id}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  {activeSlide.type === "avatar" ? (
                    <div className="account-preview-card__avatar">
                      <AvatarCreator
                        currentUser={currentUser}
                        uploadImage={uploadImage}
                      />
                    </div>
                  ) : (
                    <img src={activeSlide.url} alt="Profile preview" />
                  )}
                </motion.div>
              </AnimatePresence>

              {slides.length > 1 && (
                <>
                  <button
                    type="button"
                    className="account-preview-card__arrow is-left"
                    disabled={slide === 0}
                    onClick={() => setSlide((value) => Math.max(0, value - 1))}
                    aria-label="Previous profile image"
                  >
                    <FiChevronLeft />
                  </button>
                  <button
                    type="button"
                    className="account-preview-card__arrow is-right"
                    disabled={slide === slides.length - 1}
                    onClick={() =>
                      setSlide((value) =>
                        Math.min(slides.length - 1, value + 1),
                      )
                    }
                    aria-label="Next profile image"
                  >
                    <FiChevronRight />
                  </button>
                </>
              )}

              <div className="account-preview-card__identity">
                <strong>{form.userName || "Your name"}</strong>
                {!!form.headline && <span>{form.headline}</span>}
                <button
                  type="button"
                  className="account-preview-card__tagline"
                  onClick={openTaglineModal}
                >
                  {form.selectedTaglineKey ? (
                    <>
                      <em>is {humanizeTagline(form.selectedTaglineKey)}</em>
                      {!!tagsFromText(form.selectedTagsText).length && (
                        <small>
                          {tagsFromText(form.selectedTagsText).join(", ")}
                        </small>
                      )}
                    </>
                  ) : (
                    <em>Express yourself with a tagline</em>
                  )}
                </button>
              </div>

              {slides.length > 1 && (
                <nav aria-label="Profile preview slides">
                  {slides.map((item, index) => (
                    <button
                      type="button"
                      key={item.id}
                      className={slide === index ? "active" : ""}
                      onClick={() => setSlide(index)}
                      aria-label={`Open profile preview ${index + 1}`}
                    />
                  ))}
                </nav>
              )}
            </div>

            <div className="account-primary-media">
              <div className="account-primary-media__heading">
                <div>
                  <strong>Primary profile visual</strong>
                  <span>Choose what people see first.</span>
                </div>
              </div>

              <div className="account-primary-media__choices">
                <motion.button
                  type="button"
                  className={form.showAvatar ? "is-active" : ""}
                  onClick={() => choosePrimary(true)}
                  animate={{
                    scale: form.showAvatar ? 1.04 : 0.94,
                    y: form.showAvatar ? -4 : 0,
                    opacity: form.showAvatar ? 1 : 0.62,
                  }}
                  transition={{ type: "spring", stiffness: 330, damping: 24 }}
                >
                  <Peep
                    useravatar={currentUser.imgAvatar}
                    size={150}
                    width={120}
                    height={120}
                    borderRadius={60}
                    borderWidth={0}
                    overflow="hidden"
                    backgroundColor="transparent"
                    // tiny
                  />
                  <span>{form.showAvatar ? "Using avatar" : "Use avatar"}</span>
                  <AnimatePresence>
                    {form.showAvatar && (
                      <motion.i
                        className="account-primary-media__arrow is-avatar"
                        initial={{ opacity: 0, scale: 0.6, rotate: -12 }}
                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                      >
                        <ArrowDownRight />
                      </motion.i>
                    )}
                  </AnimatePresence>
                </motion.button>

                <motion.button
                  type="button"
                  className={!form.showAvatar ? "is-active" : ""}
                  disabled={!photos.length}
                  onClick={() => choosePrimary(false)}
                  animate={{
                    scale: !form.showAvatar && photos.length ? 1.04 : 0.94,
                    y: !form.showAvatar && photos.length ? -4 : 0,
                    opacity: !form.showAvatar && photos.length ? 1 : 0.62,
                  }}
                  transition={{ type: "spring", stiffness: 330, damping: 24 }}
                >
                  <span className="account-primary-media__photo">
                    {photos[0] ? <img src={photos[0]} alt="" /> : <FiUpload />}
                  </span>
                  <span>
                    {!photos.length
                      ? "Add a photo"
                      : form.showAvatar
                        ? "Use photo"
                        : "Using photo"}
                  </span>
                  <AnimatePresence>
                    {!form.showAvatar && photos.length > 0 && (
                      <motion.i
                        className="account-primary-media__arrow is-photo"
                        initial={{ opacity: 0, scale: 0.6, rotate: 12 }}
                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                      >
                        <ArrowDownSlightRight />
                      </motion.i>
                    )}
                  </AnimatePresence>
                </motion.button>
              </div>

              <UploadOverlay
                visible={uploading}
                progress={progress}
                status={status}
                inline
              />
              <div className="account-media-tools">
                {photos.length < 6 && (
                  <label className="account-upload-button">
                    <FiUpload />
                    <span>{uploading ? "Uploading…" : "Add photos"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      disabled={uploading || saving || photos.length >= 6}
                      onChange={uploadPhotos}
                    />
                  </label>
                )}
              </div>

              {!!photos.length && (
                <div className="account-photo-strip">
                  {photos.map((url, index) => (
                    <div
                      key={`${url}-${index}`}
                      className={slide === index + 1 ? "is-active" : ""}
                    >
                      <button
                        type="button"
                        className="account-photo-strip__preview"
                        onClick={() => setSlide(index + 1)}
                        aria-label={`Preview profile photo ${index + 1}`}
                      >
                        <img src={url} alt="" />
                      </button>
                      <button
                        type="button"
                        className="account-photo-strip__remove"
                        aria-label="Remove photo"
                        onClick={() => removePhoto(index)}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </aside>

        <section className="account-page__main">
          <header className="account-membership-header">
            <div>
              <span>Current membership</span>
              <strong>{currentEntitlement || "free"}</strong>
            </div>
            <button type="button" onClick={() => navigate("/?interested=pro")}>
              View plans
            </button>
            {(currentEntitlement !== "free" || membershipUrl) && (
              <a href={membershipUrl} target="_blank" rel="noreferrer">
                Manage membership
              </a>
            )}
          </header>

          <div className="account-page__scroll">
            <details className="account-settings-section" open>
              <summary>Public profile</summary>
              <div className="account-settings-section__content">
                <div className="account-form-grid">
                  <label>
                    Name
                    <input
                      value={form.userName}
                      onChange={(event) =>
                        setField("userName", event.target.value)
                      }
                    />
                  </label>
                  <label>
                    Email
                    <input value={currentUser.email || ""} readOnly />
                  </label>
                  <label className="wide">
                    Headline
                    <input
                      value={form.headline}
                      maxLength={120}
                      placeholder="A short description of what you do"
                      onChange={(event) =>
                        setField("headline", event.target.value)
                      }
                    />
                  </label>
                  <label className="wide">
                    Bio
                    <textarea
                      rows={6}
                      maxLength={1200}
                      value={form.bio}
                      placeholder="Tell people a little about yourself."
                      onChange={(event) => setField("bio", event.target.value)}
                    />
                  </label>
                </div>
              </div>
            </details>

            <details className="account-settings-section">
              <summary>Profile appearance</summary>
              <div className="account-settings-section__content">
                <span className="account-settings-label">Post display</span>
                <div className="account-choice-row">
                  {["grid", "list"].map((mode) => (
                    <button
                      type="button"
                      key={mode}
                      className={
                        form.profilePostsDisplay === mode ? "active" : ""
                      }
                      onClick={() => setField("profilePostsDisplay", mode)}
                    >
                      {mode === "grid" ? "Grid" : "List"}
                    </button>
                  ))}
                </div>

                <span className="account-settings-label">Theme</span>
                <div className="account-choice-row account-choice-row--themes">
                  {THEME_OPTIONS.map(([value, label]) => (
                    <button
                      type="button"
                      key={value}
                      className={theme === value ? "active" : ""}
                      onClick={() => changeTheme(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </details>

            <details className="account-settings-section account-notifications">
              <summary>Notifications</summary>
              <div className="account-settings-section__content">
                {NOTIFICATION_GROUPS.map((group) => (
                  <details key={group.title} open={group.title === "Messages"}>
                    <summary>{group.title}</summary>
                    <div className="notification-table">
                      <div className="notification-table__head">
                        <span />
                        <span>Web</span>
                        <span>Mobile</span>
                        <span>Email</span>
                      </div>
                      {group.rows.map(([label, prefix]) => (
                        <div key={prefix}>
                          <span>{label}</span>
                          {["Web", "Mob", "Email"].map((channel) => {
                            const notificationKey = `${prefix}${channel}`;
                            const supported =
                              channel !== "Email" ||
                              Object.prototype.hasOwnProperty.call(
                                form.notfSettings,
                                notificationKey,
                              );
                            return supported ? (
                              <input
                                type="checkbox"
                                key={notificationKey}
                                checked={
                                  form.notfSettings[notificationKey] !== false
                                }
                                onChange={(event) =>
                                  setNotification(
                                    notificationKey,
                                    event.target.checked,
                                  )
                                }
                              />
                            ) : (
                              <span key={notificationKey}>—</span>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            </details>

            <AccountPostsManager uid={currentUser.uid} />
          </div>

          <footer className="account-page__actionbar">
            {message && <span>{message}</span>}
            <button type="button" onClick={signOut}>
              <FiLogOut /> Sign out
            </button>
            <button
              type="button"
              className="primary"
              disabled={saving || uploading}
              onClick={save}
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </footer>
        </section>
      </div>

      {showTaglineModal && (
        <div className="account-tagline-modal" role="presentation">
          <button
            type="button"
            className="account-tagline-modal__backdrop"
            onClick={closeTaglineModal}
            aria-label="Close tagline editor"
          />
          <section
            className="account-tagline-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tagline-dialog-title"
          >
            <header>
              <div>
                <span>Profile status</span>
                <h2 id="tagline-dialog-title">Let people know</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={closeTaglineModal}
                aria-label="Close"
              >
                <FiX />
              </button>
            </header>

            <div className="account-tagline-modal__sentence">
              <strong>I am</strong>
              <select
                value={draftTaglineKey}
                onChange={(event) => chooseTagline(event.target.value)}
              >
                <option value="" disabled>
                  Choose a tagline
                </option>
                {!!draftTaglineKey &&
                  !TAGLINE_OPTIONS.some(
                    (option) => option.value === draftTaglineKey,
                  ) && (
                    <option value={draftTaglineKey}>{draftTaglineKey}</option>
                  )}
                {TAGLINE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <form
              className="account-tagline-modal__custom"
              onSubmit={(event) => {
                event.preventDefault();
                addCustomTagline();
              }}
            >
              <input
                value={customTagline}
                onChange={(event) => setCustomTagline(event.target.value)}
                placeholder="Custom tagline"
                maxLength={40}
              />
              <button
                type="submit"
                className="icon-button"
                disabled={!customTagline.trim()}
                aria-label="Use custom tagline"
              >
                <FiArrowUp />
              </button>
            </form>

            {!!draftTaglineKey && (
              <div className="account-tagline-modal__tags">
                <span>Choose one or more details</span>
                <div>
                  {(TAGS_BY_TAGLINE_KEY[draftTaglineKey] || []).map((tag) => (
                    <button
                      type="button"
                      key={tag}
                      className={draftTags.includes(tag) ? "active" : ""}
                      onClick={() => toggleDraftTag(tag)}
                    >
                      {tag}
                    </button>
                  ))}
                  {draftTags
                    .filter(
                      (tag) =>
                        !(TAGS_BY_TAGLINE_KEY[draftTaglineKey] || []).includes(
                          tag,
                        ),
                    )
                    .map((tag) => (
                      <button
                        type="button"
                        key={tag}
                        className="active"
                        onClick={() => toggleDraftTag(tag)}
                      >
                        {tag} <FiX />
                      </button>
                    ))}
                </div>
              </div>
            )}

            <form
              className="account-tagline-modal__custom"
              onSubmit={(event) => {
                event.preventDefault();
                addCustomTag();
              }}
            >
              <input
                value={customTag}
                onChange={(event) => setCustomTag(event.target.value)}
                placeholder="Add a custom detail"
                maxLength={40}
                disabled={!draftTaglineKey}
              />
              <button
                type="submit"
                className="icon-button"
                disabled={!draftTaglineKey || !customTag.trim()}
                aria-label="Add custom detail"
              >
                <FiArrowUp />
              </button>
            </form>

            {!canUseCustomTaglines && (
              <small className="account-tagline-modal__plan-note">
                Custom taglines and details require Ant Pro or Ant Business.
              </small>
            )}

            <footer>
              {!!form.selectedTaglineKey && (
                <button
                  type="button"
                  className="danger"
                  onClick={removeTagline}
                >
                  Remove status
                </button>
              )}
              <button type="button" onClick={closeTaglineModal}>
                Cancel
              </button>
              <button
                type="button"
                className="primary"
                disabled={!draftTaglineKey}
                onClick={confirmTagline}
              >
                Confirm
              </button>
            </footer>
          </section>
        </div>
      )}
    </main>
  );
}
